'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    const patterns = [/relation.*does not exist/i, /column.*does not exist/i, /function.*does not exist/i, /syntax error/i];
    return patterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── Dashboard KPI Metrics ───────────────────────────────────────────────────
export async function fetchDashboardMetrics(dateFrom?: string, dateTo?: string) {
  const supabase = createClient();
  const today = dateTo || new Date().toISOString().split('T')[0];
  const yesterday = dateFrom || new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  try {
    // Today's orders
    const { data: todayOrders, error: e1 } = await supabase
      .from('orders')
      .select('gross_amount, net_amount, quantity, order_status, brand, selling_price')
      .eq('order_date', today);
    if (e1 && isSchemaError(e1)) throw e1;

    // Yesterday's orders
    const { data: ydayOrders, error: e2 } = await supabase
      .from('orders')
      .select('gross_amount, net_amount, quantity, order_status')
      .eq('order_date', yesterday);
    if (e2 && isSchemaError(e2)) throw e2;

    // Today's ad spend
    const { data: todayAds, error: e3 } = await supabase
      .from('ad_campaigns')
      .select('ad_spend, attributed_sales, roas')
      .eq('campaign_date', today);
    if (e3 && isSchemaError(e3)) throw e3;

    // Yesterday's ad spend
    const { data: ydayAds, error: e4 } = await supabase
      .from('ad_campaigns')
      .select('ad_spend, attributed_sales')
      .eq('campaign_date', yesterday);
    if (e4 && isSchemaError(e4)) throw e4;

    // Inventory summary
    const { data: invData, error: e5 } = await supabase
      .from('inventory_snapshots')
      .select('available_stock, days_of_cover, stock_status')
      .eq('snapshot_date', today);
    if (e5 && isSchemaError(e5)) throw e5;

    // Returns & cancellations (last 7 days)
    const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
    const { data: returnOrders, error: e6 } = await supabase
      .from('orders')
      .select('gross_amount, order_status')
      .gte('order_date', sevenDaysAgo)
      .in('order_status', ['return_initiated', 'returned']);
    if (e6 && isSchemaError(e6)) throw e6;

    const { data: cancelOrders, error: e7 } = await supabase
      .from('orders')
      .select('order_status')
      .gte('order_date', sevenDaysAgo)
      .eq('order_status', 'cancelled');
    if (e7 && isSchemaError(e7)) throw e7;

    const { data: allRecentOrders, error: e8 } = await supabase
      .from('orders')
      .select('order_status')
      .gte('order_date', sevenDaysAgo);
    if (e8 && isSchemaError(e8)) throw e8;

    // Compute metrics
    const todayGross = (todayOrders || []).reduce((s, o) => s + Number(o.gross_amount), 0);
    const todayNet = (todayOrders || []).reduce((s, o) => s + Number(o.net_amount), 0);
    const todayUnits = (todayOrders || []).filter(o => o.order_status !== 'cancelled').reduce((s, o) => s + o.quantity, 0);
    const todayOrderCount = (todayOrders || []).filter(o => o.order_status !== 'cancelled').length;
    const todayASP = todayUnits > 0 ? todayGross / todayUnits : 0;

    const ydayGross = (ydayOrders || []).reduce((s, o) => s + Number(o.gross_amount), 0);
    const ydayNet = (ydayOrders || []).reduce((s, o) => s + Number(o.net_amount), 0);
    const ydayUnits = (ydayOrders || []).filter(o => o.order_status !== 'cancelled').reduce((s, o) => s + o.quantity, 0);
    const ydayOrderCount = (ydayOrders || []).filter(o => o.order_status !== 'cancelled').length;
    const ydayASP = ydayUnits > 0 ? ydayGross / ydayUnits : 0;

    const todayAdSpend = (todayAds || []).reduce((s, a) => s + Number(a.ad_spend), 0);
    const todayAdSales = (todayAds || []).reduce((s, a) => s + Number(a.attributed_sales), 0);
    const todayROAS = todayAdSpend > 0 ? todayAdSales / todayAdSpend : 0;

    const ydayAdSpend = (ydayAds || []).reduce((s, a) => s + Number(a.ad_spend), 0);
    const ydayAdSales = (ydayAds || []).reduce((s, a) => s + Number(a.attributed_sales), 0);

    const totalStock = (invData || []).reduce((s, r) => s + r.available_stock, 0);
    const avgDOC = invData && invData.length > 0
      ? (invData.reduce((s, r) => s + Number(r.days_of_cover), 0) / invData.length)
      : 0;
    const stockoutSkus = (invData || []).filter(r => r.stock_status === 'stockout').length;

    const returnValue = (returnOrders || []).reduce((s, o) => s + Number(o.gross_amount), 0);
    const totalRecentOrders = (allRecentOrders || []).length;
    const cancelCount = (cancelOrders || []).length;
    const cancellationRate = totalRecentOrders > 0 ? (cancelCount / totalRecentOrders) * 100 : 0;

    // Gross profit estimate (net - cost ~30% of net)
    const grossProfit = todayNet * 0.18;

    const pctChange = (curr: number, prev: number) =>
      prev > 0 ? Math.round(((curr - prev) / prev) * 100 * 10) / 10 : 0;

    const fmt = (val: number) => {
      if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)}Cr`;
      if (val >= 100000) return `₹${(val / 100000).toFixed(2)}L`;
      if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
      return `₹${val.toFixed(0)}`;
    };

    return {
      grossSales: { value: fmt(todayGross), raw: todayGross, change: pctChange(todayGross, ydayGross), label: 'vs yesterday' },
      netSales: { value: fmt(todayNet), raw: todayNet, change: pctChange(todayNet, ydayNet), label: 'vs yesterday' },
      unitsSold: { value: todayUnits.toLocaleString('en-IN'), raw: todayUnits, change: pctChange(todayUnits, ydayUnits), label: 'vs yesterday' },
      ordersReceived: { value: todayOrderCount.toLocaleString('en-IN'), raw: todayOrderCount, change: pctChange(todayOrderCount, ydayOrderCount), label: 'vs yesterday' },
      avgSellingPrice: { value: `₹${Math.round(todayASP).toLocaleString('en-IN')}`, raw: todayASP, change: pctChange(todayASP, ydayASP), label: 'vs yesterday' },
      totalStock: { value: totalStock.toLocaleString('en-IN'), raw: totalStock, change: 0, label: 'units available' },
      daysOfInventory: { value: avgDOC.toFixed(1), raw: avgDOC, change: 0, label: 'days avg' },
      returns: { value: fmt(returnValue), raw: returnValue, change: 0, label: 'last 7 days' },
      cancellationRate: { value: `${cancellationRate.toFixed(1)}%`, raw: cancellationRate, change: 0, label: 'last 7 days' },
      adSpend: { value: fmt(todayAdSpend), raw: todayAdSpend, change: pctChange(todayAdSpend, ydayAdSpend), label: 'vs yesterday' },
      adSales: { value: fmt(todayAdSales), raw: todayAdSales, change: pctChange(todayAdSales, ydayAdSales), label: 'vs yesterday' },
      roas: { value: `${todayROAS.toFixed(2)}×`, raw: todayROAS, change: 0, label: 'today' },
      grossProfit: { value: fmt(grossProfit), raw: grossProfit, change: pctChange(grossProfit, ydayNet * 0.18), label: 'est. margin' },
      pendingSettlements: { value: fmt(todayNet * 0.23), raw: todayNet * 0.23, change: 0, label: 'awaiting' },
      stockoutSkus: { value: String(stockoutSkus), raw: stockoutSkus, change: 0, label: 'SKUs at zero stock' },
    };
  } catch (err: any) {
    console.error('fetchDashboardMetrics error:', err.message);
    return null;
  }
}

// ─── Sales Trend (last 30 days) ───────────────────────────────────────────────
export async function fetchSalesTrend(days = 30) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('order_date, gross_amount, quantity, order_status')
      .gte('order_date', from)
      .neq('order_status', 'cancelled')
      .order('order_date', { ascending: true });
    if (error && isSchemaError(error)) throw error;

    // Group by date
    const map: Record<string, { revenue: number; units: number }> = {};
    (data || []).forEach((o) => {
      const d = o.order_date;
      if (!map[d]) map[d] = { revenue: 0, units: 0 };
      map[d].revenue += Number(o.gross_amount);
      map[d].units += o.quantity;
    });

    return Object.entries(map).map(([date, vals]) => ({
      date: new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      revenue: Math.round(vals.revenue),
      units: vals.units,
    }));
  } catch (err: any) {
    console.error('fetchSalesTrend error:', err.message);
    return null;
  }
}

// ─── Brand Revenue ────────────────────────────────────────────────────────────
export async function fetchBrandRevenue(days = 1) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('brand, gross_amount, quantity, order_status')
      .gte('order_date', from)
      .neq('order_status', 'cancelled');
    if (error && isSchemaError(error)) throw error;

    const map: Record<string, { value: number; units: number }> = {};
    (data || []).forEach((o) => {
      if (!map[o.brand]) map[o.brand] = { value: 0, units: 0 };
      map[o.brand].value += Number(o.gross_amount);
      map[o.brand].units += o.quantity;
    });

    return Object.entries(map).map(([name, v]) => ({ name, value: Math.round(v.value), units: v.units }));
  } catch (err: any) {
    console.error('fetchBrandRevenue error:', err.message);
    return null;
  }
}

// ─── Warehouse Revenue ────────────────────────────────────────────────────────
export async function fetchWarehouseRevenue(days = 30) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('warehouse, gross_amount, quantity, order_status')
      .gte('order_date', from)
      .neq('order_status', 'cancelled');
    if (error && isSchemaError(error)) throw error;

    const map: Record<string, { revenue: number; units: number }> = {};
    (data || []).forEach((o) => {
      if (!map[o.warehouse]) map[o.warehouse] = { revenue: 0, units: 0 };
      map[o.warehouse].revenue += Number(o.gross_amount);
      map[o.warehouse].units += o.quantity;
    });

    return Object.entries(map).map(([name, v]) => ({ name, revenue: Math.round(v.revenue), units: v.units }));
  } catch (err: any) {
    console.error('fetchWarehouseRevenue error:', err.message);
    return null;
  }
}

// ─── Ad Spend vs Sales ────────────────────────────────────────────────────────
export async function fetchAdSpendVsSales(days = 30) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('ad_campaigns')
      .select('campaign_date, ad_spend, attributed_sales')
      .gte('campaign_date', from)
      .order('campaign_date', { ascending: true });
    if (error && isSchemaError(error)) throw error;

    const map: Record<string, { adSpend: number; adSales: number }> = {};
    (data || []).forEach((r) => {
      const d = r.campaign_date;
      if (!map[d]) map[d] = { adSpend: 0, adSales: 0 };
      map[d].adSpend += Number(r.ad_spend);
      map[d].adSales += Number(r.attributed_sales);
    });

    return Object.entries(map).map(([date, v]) => ({
      date: new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      adSpend: Math.round(v.adSpend),
      adSales: Math.round(v.adSales),
    }));
  } catch (err: any) {
    console.error('fetchAdSpendVsSales error:', err.message);
    return null;
  }
}

// ─── Top Products ─────────────────────────────────────────────────────────────
export async function fetchTopProducts(days = 30, limit = 8) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('sku, product_name, brand, gross_amount, net_amount, quantity, order_status')
      .gte('order_date', from)
      .neq('order_status', 'cancelled');
    if (error && isSchemaError(error)) throw error;

    const { data: adData, error: e2 } = await supabase
      .from('ad_campaigns')
      .select('sku, ad_spend, attributed_sales')
      .gte('campaign_date', from);
    if (e2 && isSchemaError(e2)) throw e2;

    const adMap: Record<string, { spend: number; sales: number }> = {};
    (adData || []).forEach((a) => {
      if (!a.sku) return;
      if (!adMap[a.sku]) adMap[a.sku] = { spend: 0, sales: 0 };
      adMap[a.sku].spend += Number(a.ad_spend);
      adMap[a.sku].sales += Number(a.attributed_sales);
    });

    const skuMap: Record<string, { brand: string; name: string; units: number; gross: number; net: number }> = {};
    (data || []).forEach((o) => {
      if (!skuMap[o.sku]) skuMap[o.sku] = { brand: o.brand, name: o.product_name, units: 0, gross: 0, net: 0 };
      skuMap[o.sku].units += o.quantity;
      skuMap[o.sku].gross += Number(o.gross_amount);
      skuMap[o.sku].net += Number(o.net_amount);
    });

    return Object.entries(skuMap)
      .map(([sku, v]) => {
        const ad = adMap[sku] || { spend: 0, sales: 0 };
        const roas = ad.spend > 0 ? Math.round((ad.sales / ad.spend) * 100) / 100 : 0;
        const margin = v.gross > 0 ? Math.round(((v.net - v.gross * 0.7) / v.gross) * 1000) / 10 : 0;
        return { id: sku, brand: v.brand, name: v.name, sku, units: v.units, revenue: Math.round(v.gross), roas, margin };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, limit);
  } catch (err: any) {
    console.error('fetchTopProducts error:', err.message);
    return null;
  }
}

// ─── Smart Alerts ─────────────────────────────────────────────────────────────
export async function fetchSmartAlerts() {
  const supabase = createClient();
  const today = new Date().toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('inventory_snapshots')
      .select('sku, warehouse, stock_status, days_of_cover, age_in_days, available_stock')
      .eq('snapshot_date', today);
    if (error && isSchemaError(error)) throw error;

    const alerts: Array<{ id: string; type: string; severity: string; message: string; detail: string }> = [];
    (data || []).forEach((r) => {
      if (r.stock_status === 'stockout') {
        alerts.push({ id: `so-${r.sku}-${r.warehouse}`, type: 'stockout', severity: 'critical', message: `${r.sku} — Stockout at ${r.warehouse}`, detail: 'Zero available stock' });
      } else if (r.stock_status === 'critical' || (r.days_of_cover > 0 && r.days_of_cover < 3)) {
        alerts.push({ id: `cr-${r.sku}-${r.warehouse}`, type: 'critical', severity: 'critical', message: `${r.sku} — ${r.days_of_cover.toFixed(1)} days cover at ${r.warehouse}`, detail: 'Urgent replenishment needed' });
      } else if (r.age_in_days > 90) {
        alerts.push({ id: `ag-${r.sku}-${r.warehouse}`, type: 'ageing', severity: 'warning', message: `${r.sku} — ${r.age_in_days} days old at ${r.warehouse}`, detail: `${r.available_stock} units at risk` });
      }
    });

    return alerts.slice(0, 8);
  } catch (err: any) {
    console.error('fetchSmartAlerts error:', err.message);
    return null;
  }
}

// ─── Inventory Alert Summary ──────────────────────────────────────────────────
export async function fetchInventoryAlertSummary() {
  const supabase = createClient();
  const today = new Date().toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('inventory_snapshots')
      .select('sku, warehouse, stock_status, days_of_cover, age_in_days, available_stock')
      .eq('snapshot_date', today);
    if (error && isSchemaError(error)) throw error;

    const rows = data || [];
    const stockout = rows.filter(r => r.stock_status === 'stockout');
    const critical3 = rows.filter(r => r.days_of_cover > 0 && r.days_of_cover < 3 && r.stock_status !== 'stockout');
    const low7 = rows.filter(r => r.days_of_cover >= 3 && r.days_of_cover < 7);
    const aged90 = rows.filter(r => r.age_in_days > 90);

    return {
      stockout: { count: stockout.length, items: stockout.map(r => `${r.sku} (${r.warehouse.substring(0,3).toUpperCase()})`) },
      critical3: { count: critical3.length, items: critical3.map(r => `${r.sku} (${r.warehouse.substring(0,3).toUpperCase()})`) },
      low7: { count: low7.length, items: low7.map(r => `${r.sku} (${r.warehouse.substring(0,3).toUpperCase()})`) },
      aged90: { count: aged90.length, items: aged90.map(r => `${r.sku} (${r.warehouse.substring(0,3).toUpperCase()}) — ${r.available_stock} units`) },
    };
  } catch (err: any) {
    console.error('fetchInventoryAlertSummary error:', err.message);
    return null;
  }
}

// ─── Inventory Table Data ─────────────────────────────────────────────────────
export async function fetchInventoryTable() {
  const supabase = createClient();
  const today = new Date().toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('inventory_snapshots')
      .select('*, products(brand, product_name, model, fsn)')
      .eq('snapshot_date', today)
      .order('days_of_cover', { ascending: true });
    if (error && isSchemaError(error)) throw error;

    return (data || []).map((r: any) => ({
      id: r.id,
      brand: r.products?.brand || '',
      product: r.products?.product_name || r.sku,
      model: r.products?.model || '',
      sku: r.sku,
      fsn: r.products?.fsn || '',
      warehouse: r.warehouse,
      imei: true,
      opening: r.opening_stock,
      available: r.available_stock,
      reserved: r.reserved_stock,
      inTransit: r.in_transit,
      blocked: r.blocked_stock,
      damaged: r.damaged_stock,
      closing: r.closing_stock,
      soldToday: r.sold_today,
      avgDailySales: Number(r.avg_daily_sales),
      daysOfCover: Number(r.days_of_cover),
      lastInward: r.last_inward_date ? new Date(r.last_inward_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
      ageInDays: r.age_in_days,
      reorderLevel: r.reorder_level,
      replenishQty: r.replenish_qty,
      status: r.stock_status,
    }));
  } catch (err: any) {
    console.error('fetchInventoryTable error:', err.message);
    return null;
  }
}

// ─── Inventory Charts ─────────────────────────────────────────────────────────
export async function fetchInventoryCharts() {
  const supabase = createClient();
  const today = new Date().toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('inventory_snapshots')
      .select('warehouse, available_stock, reserved_stock, in_transit, blocked_stock, age_in_days')
      .eq('snapshot_date', today);
    if (error && isSchemaError(error)) throw error;

    const whMap: Record<string, { available: number; reserved: number; inTransit: number; blocked: number }> = {};
    (data || []).forEach((r) => {
      if (!whMap[r.warehouse]) whMap[r.warehouse] = { available: 0, reserved: 0, inTransit: 0, blocked: 0 };
      whMap[r.warehouse].available += r.available_stock;
      whMap[r.warehouse].reserved += r.reserved_stock;
      whMap[r.warehouse].inTransit += r.in_transit;
      whMap[r.warehouse].blocked += r.blocked_stock;
    });

    const warehouseStock = Object.entries(whMap).map(([name, v]) => ({ name, ...v }));

    // Ageing buckets
    const buckets = [
      { bucket: '0–15 days', min: 0, max: 15 },
      { bucket: '16–30 days', min: 16, max: 30 },
      { bucket: '31–60 days', min: 31, max: 60 },
      { bucket: '61–90 days', min: 61, max: 90 },
      { bucket: '>90 days', min: 91, max: Infinity },
    ];
    const totalUnits = (data || []).reduce((s, r) => s + r.available_stock, 0);
    const ageingData = buckets.map((b) => {
      const units = (data || [])
        .filter(r => r.age_in_days >= b.min && r.age_in_days <= b.max)
        .reduce((s, r) => s + r.available_stock, 0);
      return { bucket: b.bucket, units, pct: totalUnits > 0 ? Math.round((units / totalUnits) * 1000) / 10 : 0 };
    });

    return { warehouseStock, ageingData, totalUnits };
  } catch (err: any) {
    console.error('fetchInventoryCharts error:', err.message);
    return null;
  }
}

// ─── Sales KPIs ───────────────────────────────────────────────────────────────
export async function fetchSalesKpis(days = 30) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  const prevFrom = new Date(Date.now() - days * 2 * 86400000).toISOString().split('T')[0];
  try {
    const { data: curr, error: e1 } = await supabase
      .from('orders')
      .select('gross_amount, net_amount, quantity, order_status')
      .gte('order_date', from);
    if (e1 && isSchemaError(e1)) throw e1;

    const { data: prev, error: e2 } = await supabase
      .from('orders')
      .select('gross_amount, net_amount, quantity, order_status')
      .gte('order_date', prevFrom)
      .lt('order_date', from);
    if (e2 && isSchemaError(e2)) throw e2;

    const calc = (rows: any[]) => {
      const active = rows.filter(o => o.order_status !== 'cancelled');
      const gross = active.reduce((s, o) => s + Number(o.gross_amount), 0);
      const net = active.reduce((s, o) => s + Number(o.net_amount), 0);
      const units = active.reduce((s, o) => s + o.quantity, 0);
      const asp = units > 0 ? gross / units : 0;
      const returns = rows.filter(o => ['return_initiated', 'returned'].includes(o.order_status)).length;
      const cancelled = rows.filter(o => o.order_status === 'cancelled').length;
      const returnRate = rows.length > 0 ? (returns / rows.length) * 100 : 0;
      const cancelRate = rows.length > 0 ? (cancelled / rows.length) * 100 : 0;
      return { gross, net, units, asp, returnRate, cancelRate };
    };

    const c = calc(curr || []);
    const p = calc(prev || []);
    const pct = (a: number, b: number) => b > 0 ? Math.round(((a - b) / b) * 1000) / 10 : 0;
    const fmt = (v: number) => v >= 100000 ? `₹${(v / 100000).toFixed(2)}L` : `₹${v.toFixed(0)}`;

    return [
      { id: 'skpi-gross', label: 'Gross Revenue', value: fmt(c.gross), change: pct(c.gross, p.gross), changeLabel: 'vs prev period' },
      { id: 'skpi-net', label: 'Net Revenue', value: fmt(c.net), change: pct(c.net, p.net), changeLabel: 'vs prev period' },
      { id: 'skpi-units', label: 'Units Sold', value: c.units.toLocaleString('en-IN'), change: pct(c.units, p.units), changeLabel: 'vs prev period' },
      { id: 'skpi-asp', label: 'Avg Selling Price', value: `₹${Math.round(c.asp).toLocaleString('en-IN')}`, change: pct(c.asp, p.asp), changeLabel: 'vs prev period' },
      { id: 'skpi-returns', label: 'Return Rate', value: `${c.returnRate.toFixed(1)}%`, change: pct(c.returnRate, p.returnRate), changeLabel: 'vs prev period' },
      { id: 'skpi-cancel', label: 'Cancellation Rate', value: `${c.cancelRate.toFixed(1)}%`, change: pct(c.cancelRate, p.cancelRate), changeLabel: 'vs prev period' },
    ];
  } catch (err: any) {
    console.error('fetchSalesKpis error:', err.message);
    return null;
  }
}

// ─── Sales Revenue Trend ──────────────────────────────────────────────────────
export async function fetchSalesRevenueTrend(days = 30) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('order_date, gross_amount, net_amount, order_status')
      .gte('order_date', from)
      .neq('order_status', 'cancelled')
      .order('order_date', { ascending: true });
    if (error && isSchemaError(error)) throw error;

    const map: Record<string, { gross: number; net: number }> = {};
    (data || []).forEach((o) => {
      const d = o.order_date;
      if (!map[d]) map[d] = { gross: 0, net: 0 };
      map[d].gross += Number(o.gross_amount);
      map[d].net += Number(o.net_amount);
    });

    return Object.entries(map).map(([date, v]) => ({
      date: new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      gross: Math.round(v.gross),
      net: Math.round(v.net),
    }));
  } catch (err: any) {
    console.error('fetchSalesRevenueTrend error:', err.message);
    return null;
  }
}

// ─── SKU Performance ──────────────────────────────────────────────────────────
export async function fetchSkuPerformance(days = 30) {
  const supabase = createClient();
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('sku, product_name, brand, gross_amount, net_amount, flipkart_fee, logistics_fee, quantity, order_status')
      .gte('order_date', from)
      .neq('order_status', 'cancelled');
    if (error && isSchemaError(error)) throw error;

    const { data: adData, error: e2 } = await supabase
      .from('ad_campaigns')
      .select('sku, ad_spend')
      .gte('campaign_date', from);
    if (e2 && isSchemaError(e2)) throw e2;

    const adMap: Record<string, number> = {};
    (adData || []).forEach((a) => {
      if (!a.sku) return;
      adMap[a.sku] = (adMap[a.sku] || 0) + Number(a.ad_spend);
    });

    const skuMap: Record<string, any> = {};
    (data || []).forEach((o) => {
      if (!skuMap[o.sku]) skuMap[o.sku] = { brand: o.brand, product: o.product_name, sku: o.sku, units: 0, grossRev: 0, flipFee: 0, logistic: 0, netRev: 0 };
      skuMap[o.sku].units += o.quantity;
      skuMap[o.sku].grossRev += Number(o.gross_amount);
      skuMap[o.sku].flipFee += Number(o.flipkart_fee);
      skuMap[o.sku].logistic += Number(o.logistics_fee);
      skuMap[o.sku].netRev += Number(o.net_amount);
    });

    return Object.entries(skuMap).map(([sku, v]: [string, any]) => {
      const adCost = adMap[sku] || 0;
      const netPerUnit = v.units > 0 ? Math.round(v.netRev / v.units) : 0;
      const margin = v.grossRev > 0 ? Math.round(((v.netRev - v.grossRev * 0.65) / v.grossRev) * 1000) / 10 : 0;
      const profitStatus = margin >= 12 ? 'profitable' : margin >= 6 ? 'low-margin' : margin >= 0 ? 'break-even' : 'loss';
      return { id: `skut-${sku}`, brand: v.brand, product: v.product, sku, units: v.units, grossRev: Math.round(v.grossRev), flipFee: Math.round(v.flipFee), logistic: Math.round(v.logistic), adCost: Math.round(adCost), netRev: Math.round(v.netRev), netPerUnit, margin, profitStatus };
    }).sort((a, b) => b.grossRev - a.grossRev);
  } catch (err: any) {
    console.error('fetchSkuPerformance error:', err.message);
    return null;
  }
}

// ─── Recent Orders ────────────────────────────────────────────────────────────
export async function fetchRecentOrders(limit = 10) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('order_ref, product_name, sku, warehouse, quantity, gross_amount, order_status, order_time')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error && isSchemaError(error)) throw error;

    return (data || []).map((o) => ({
      id: o.order_ref,
      product: o.product_name,
      sku: o.sku,
      warehouse: o.warehouse,
      qty: o.quantity,
      amount: Math.round(Number(o.gross_amount)),
      status: o.order_status?.replace('_', '-') || 'processing',
      time: o.order_time ? o.order_time.substring(0, 5) : '--:--',
    }));
  } catch (err: any) {
    console.error('fetchRecentOrders error:', err.message);
    return null;
  }
}

// ─── Returns & Cancellations Trend ───────────────────────────────────────────
export async function fetchReturnsCancellationsTrend(weeks = 8) {
  const supabase = createClient();
  const from = new Date(Date.now() - weeks * 7 * 86400000).toISOString().split('T')[0];
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('order_date, order_status')
      .gte('order_date', from)
      .order('order_date', { ascending: true });
    if (error && isSchemaError(error)) throw error;

    // Group by week
    const weekMap: Record<string, { total: number; returns: number; cancels: number }> = {};
    (data || []).forEach((o) => {
      const d = new Date(o.order_date);
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay());
      const key = weekStart.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      if (!weekMap[key]) weekMap[key] = { total: 0, returns: 0, cancels: 0 };
      weekMap[key].total++;
      if (['return_initiated', 'returned'].includes(o.order_status)) weekMap[key].returns++;
      if (o.order_status === 'cancelled') weekMap[key].cancels++;
    });

    return Object.entries(weekMap).map(([week, v]) => ({
      week,
      returnRate: v.total > 0 ? Math.round((v.returns / v.total) * 1000) / 10 : 0,
      cancelRate: v.total > 0 ? Math.round((v.cancels / v.total) * 1000) / 10 : 0,
    }));
  } catch (err: any) {
    console.error('fetchReturnsCancellationsTrend error:', err.message);
    return null;
  }
}
